import React, { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Car } from '../../types';
import { findCodePrefix, nextSequence } from '../../utils/codePrefixUtils';
import Input from '../Form/Input';
import Select from '../Form/Select';
import Button from '../Form/Button';
import { useCodePrefixes } from '../../hooks/useCodePrefixes';
import { useBranches } from '../../hooks/useBranches';
import { uploadFiles } from '../../services/upload.service';
import { CarCodePrefixField, CarDocumentUpload } from './CarFormSections';

export interface PendingCarDocument {
  documentType: string;
  documentNumber?: string;
  expiryDate?: string;
  filePath: string;
}

// Document Settings matches types by canonical name (case/qualifier
// insensitive), so the stored value is the plain English name.
const carDocumentTypeLabels: Record<string, string> = {
  license: 'Vehicle License',
  registration: 'Registration',
  insurance: 'Insurance',
  inspection: 'Inspection Certificate',
  ownership: 'Ownership Document',
  other: 'Other',
};

interface CarFormData {
  code: string;
  ownerName: string;
  plateNumber: string;
  chassisNumber: string;
  engineNumber: string;
  manufacturingYear: string;
  licenseExpiry: string;
  branchId: string;
  status: 'active' | 'inactive';
}

interface CarFormProps {
  car?: Car;
  onSubmit: (data: Partial<Car>, pendingDocument?: PendingCarDocument) => void;
  onCancel: () => void;
}

const CarForm: React.FC<CarFormProps> = ({ car, onSubmit, onCancel }) => {
  const { data: branches = [] } = useBranches();
  const [selectedCodePrefix, setSelectedCodePrefix] = useState<string>('');
  const [carNumber, setCarNumber] = useState<string>('001');
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState<string>('');
  const [uploadError, setUploadError] = useState<string>('');

  const queryOptions = useMemo(() => ({
    sort: [{ field: 'createdAt', order: 'desc' as const }],
    pagination: { pageSize: 100 }
  }), []);
  const { data: codePrefixes = [] } = useCodePrefixes(queryOptions);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
    reset
  } = useForm<CarFormData>({
    defaultValues: car ? {
      code: car.code,
      ownerName: car.ownerName,
      plateNumber: car.plateNumber,
      chassisNumber: car.chassisNumber,
      engineNumber: car.engineNumber,
      manufacturingYear: car.manufacturingYear,
      licenseExpiry: car.licenseExpiry,
      branchId: car.branchId,
      status: car.status
    } : {
      code: '',
      ownerName: '',
      plateNumber: '',
      chassisNumber: '',
      engineNumber: '',
      manufacturingYear: '',
      licenseExpiry: '',
      branchId: '',
      status: 'active'
    }
  });

  // Re-apply the car values whenever an async option list finishes loading:
  // the vessel select is uncontrolled, so a value set before its option
  // exists silently falls back to the first option.
  useEffect(() => {
    if (car) {
      reset(car);
      if (car.code) {
        const prefix = codePrefixes.find((p: any) => car.code.startsWith(p.prefix));
        if (prefix) {
          setSelectedCodePrefix(prefix.id);
          setCarNumber(car.code.replace(prefix.prefix, ''));
        }
      }
    }
  }, [car, reset, codePrefixes, branches]);

  const onFormSubmit = async (data: CarFormData) => {
    setUploadError('');
    let pendingDocument: PendingCarDocument | undefined;
    if (documentFile) {
      if (!documentType) {
        setUploadError('Please select the document type before saving (اختر نوع الوثيقة)');
        return;
      }
      try {
        const [uploaded] = await uploadFiles([documentFile]);
        pendingDocument = {
          documentType: carDocumentTypeLabels[documentType] || documentType,
          documentNumber: data.plateNumber || undefined,
          expiryDate: data.licenseExpiry || undefined,
          filePath: uploaded.url,
        };
      } catch (e: any) {
        setUploadError(e?.message || 'Document upload failed');
        return;
      }
    }
    onSubmit(data, pendingDocument);
  };

  const handlePrefixChange = (prefixId: string) => {
    setSelectedCodePrefix(prefixId);
    const prefix = findCodePrefix(codePrefixes as any[], prefixId);
    if (prefix) {
      const nextNum = String(nextSequence(prefix)).padStart(3, '0');
      setCarNumber(nextNum);
      setValue('code', `${prefix.prefix}${nextNum}`, { shouldValidate: true });
    }
  };

  const branchOptions = (branches as any[]).map((branch: any) => ({
    value: branch.documentId || branch.id,
    label: branch.name
  }));

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 30 }, (_, i) => {
    const year = currentYear - i;
    return { value: year.toString(), label: year.toString() };
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-xl font-semibold text-gray-900">
            {car ? 'Edit Car' : 'Add New Car'}
          </h2>
        </div>
        
        <form onSubmit={handleSubmit(onFormSubmit)} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="col-span-full">
              <h3 className="text-lg font-medium text-gray-900 mb-4 border-b pb-2">
                Car Information
              </h3>
            </div>
            
            <CarCodePrefixField
              selectedCodePrefix={selectedCodePrefix}
              carNumber={carNumber}
              codePrefixes={codePrefixes}
              errors={errors}
              onPrefixChange={handlePrefixChange}
              registerCode={register('code')}
            />
            
            <Input label="Owner Name" {...register('ownerName')} error={errors.ownerName?.message} placeholder="Ahmed Hassan" />
            <Input label="Plate Number" {...register('plateNumber')} error={errors.plateNumber?.message} placeholder="ABC-123" />
            <Input label="Chassis Number" {...register('chassisNumber')} error={errors.chassisNumber?.message} placeholder="1234567890" />
            <Input label="Engine Number" {...register('engineNumber')} error={errors.engineNumber?.message} placeholder="ENG123456" />
            <Select label="Manufacturing Year" {...register('manufacturingYear')} options={yearOptions} error={errors.manufacturingYear?.message} />
            <Input label="License Expiry Date" type="date" {...register('licenseExpiry')} error={errors.licenseExpiry?.message} />
            
            <CarDocumentUpload
              documentType={documentType}
              licenseFileName={documentFile?.name || ''}
              onDocumentTypeChange={setDocumentType}
              onFileChange={(e) => {
                setDocumentFile(e.target.files?.[0] || null);
              }}
            />
            
            <Select label="Vessel/Headquarters" {...register('branchId')} options={branchOptions} error={errors.branchId?.message} />
            <Select
              label="Status"
              {...register('status')}
              options={[
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' }
              ]}
            />
          </div>

          {uploadError && (
            <p className="mt-4 text-sm text-red-600">{uploadError}</p>
          )}

          <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 mt-8 pt-6 border-t border-gray-200">
            <Button type="button" variant="secondary" onClick={onCancel} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto">
              {isSubmitting ? 'Saving...' : car ? 'Update Car' : 'Add Car'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CarForm;
